import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { orderApi } from '../services/api';

const money = (value) => Number(value || 0).toFixed(2);

const getOrderBreakdown = (order) => {
  const subtotal = Number(order?.subtotalAmount ?? 0) ||
    (order?.items || []).reduce((sum, item) => {
      return sum + Number(item?.price || 0) * Number(item?.quantity || 0);
    }, 0);
  const delivery = Number(order?.deliveryCharge || 0);
  const wallet = Number(order?.walletAmount || 0);
  const payable = Number(order?.amountToPayByCard ?? order?.totalAmount ?? 0);

  return { subtotal, delivery, wallet, payable };
};

const OrderConfirmation = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (id) fetchOrder();
  }, [id]);

  const fetchOrder = async () => {
    try {
      const res = await orderApi.getById(id);

      // 🔥 SAFE HANDLING (fix backend inconsistency)
      const orderData = res.data?.order || res.data;

      setOrder(orderData);

    } catch (err) {
      console.error('Error fetching order:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <h2 style={{ textAlign: 'center' }}>Loading order details...</h2>;
  }

  if (!order) {
    return <h2 style={{ textAlign: 'center' }}>Order not found</h2>;
  }

  const breakdown = getOrderBreakdown(order);

  return (
    <div style={styles.container}>

      {/* Success Header */}
      <div style={styles.successBox}>
        <h1 style={{ color: 'green' }}>✅ Order Placed Successfully!</h1>
        <p>Your order has been confirmed.</p>
        <h3>Order ID: {order?._id || 'N/A'}</h3>
      </div>

      {/* Items */}
      <div style={styles.section}>
        <h2>🛒 Items</h2>

        {(order?.items || []).length === 0 ? (
          <p>No items found</p>
        ) : (
          order.items.map((item, index) => (
            <div key={index} style={styles.item}>
              <img
                src={item?.image || 'https://via.placeholder.com/80'}
                alt={item?.name || 'product'}
                style={styles.image}
              />
              <div>
                <h4>{item?.name || 'Unnamed Product'}</h4>
                <p>Quantity: {item?.quantity || 0}</p>
                <p>Price: ₹{item?.price || 0}</p>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Shipping */}
      <div style={styles.section}>
        <h2>📦 Shipping Details</h2>
        <p>
          <strong>Name:</strong>{" "}
          {order?.shippingAddress?.firstName || ''}{" "}
          {order?.shippingAddress?.lastName || ''}
        </p>
        <p><strong>Address:</strong> {order?.shippingAddress?.address || 'N/A'}</p>
        <p><strong>City:</strong> {order?.shippingAddress?.city || 'N/A'}</p>
        <p><strong>State:</strong> {order?.shippingAddress?.state || 'N/A'}</p>
        <p><strong>Pincode:</strong> {order?.shippingAddress?.postalCode || 'N/A'}</p>
      </div>

      {/* Payment */}
      <div style={styles.section}>
        <h2>💳 Payment</h2>
        <p><strong>Method:</strong> {order?.paymentMethod || 'N/A'}</p>
        <p><strong>Status:</strong> {order?.paymentStatus || 'Pending'}</p>
      </div>

      {/* Payment Breakdown */}
      {(
        <div style={styles.section}>
          <h2>💰 Payment Breakdown</h2>
          <p><strong>Subtotal:</strong> Rs.{money(breakdown.subtotal)}</p>
          <p><strong>Delivery:</strong> {breakdown.delivery === 0 ? 'Free' : `Rs.${money(breakdown.delivery)}`}</p>
          {breakdown.wallet > 0 && <p><strong>Wallet Used:</strong> Rs.{money(breakdown.wallet)}</p>}
          <p><strong>Amount to Pay:</strong> Rs.{money(breakdown.payable)}</p>
        </div>
      )}

      {/* Total */}
      <div style={styles.total}>
        <h2>Amount to Pay: Rs.{money(breakdown.payable)}</h2>
      </div>

      {/* Button */}
      <button style={styles.button} onClick={() => navigate('/')}>
        Continue Shopping
      </button>

    </div>
  );
};

const styles = {
  container: {
    maxWidth: '800px',
    margin: '40px auto',
    padding: '20px',
    fontFamily: 'Arial',
  },
  successBox: {
    textAlign: 'center',
    marginBottom: '30px',
  },
  section: {
    marginBottom: '25px',
    padding: '15px',
    border: '1px solid #ddd',
    borderRadius: '10px',
    background: '#fafafa'
  },
  item: {
    display: 'flex',
    gap: '15px',
    marginBottom: '12px',
    alignItems: 'center'
  },
  image: {
    width: '80px',
    height: '80px',
    objectFit: 'cover',
    borderRadius: '8px'
  },
  total: {
    textAlign: 'right',
    marginTop: '20px',
    fontWeight: 'bold'
  },
  button: {
    width: '100%',
    padding: '12px',
    backgroundColor: '#8B0000',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    marginTop: '20px',
  }
};

export default OrderConfirmation;
